import React from 'react';
import Sketch from 'react-p5';

class Bone {
  constructor(p5, img, x, isMobile) {
    this.p5 = p5;
    this.img = img;
    this.x = x;
    this.y = -100;
    
    // Simplified mobile optimization - focus on fewer, simpler bones
    if (isMobile) {
      this.speed = p5.random(2, 4); // Much faster fall to reduce screen time
      this.size = p5.random(35, 50); // Bigger bones on mobile
      this.rotationSpeed = p5.random(-1, 1); // Slow rotation on mobile
    } else {
      this.speed = p5.random(1, 3);
      this.size = p5.random(60, 100);
      this.rotationSpeed = p5.random(-2, 2);
    }
    
    this.rotation = p5.random(-45, 45);
    this.fadeStart = this.p5.height * 0.7; // Start fading much earlier
    this.fadeEnd = this.p5.height * 0.95; // Quick fade
  }

  update() {
    this.y += this.speed;
    this.rotation += this.rotationSpeed; // Always rotate, but slower on mobile
  }

  display() {
    // Skip rendering if bone is off screen
    if (this.y < -100 || this.y > this.p5.height + 50) {
      return;
    }

    this.p5.push();
    this.p5.translate(this.x + this.size/2, this.y + this.size/2);
    this.p5.rotate(this.rotation); // Always apply rotation
    this.p5.imageMode(this.p5.CENTER);

    // Simple fade calculation
    let alpha = 255;
    if (this.y > this.fadeStart) {
      alpha = this.p5.map(this.y, this.fadeStart, this.fadeEnd, 255, 0);
      alpha = this.p5.constrain(alpha, 0, 255);
    }
    
    this.p5.tint(255, alpha);
    this.p5.image(this.img, 0, 0, this.size, this.size);
    this.p5.pop();
  }

  isOffScreen() {
    return this.y > this.p5.height + 50;
  }
}

const BoneSketch = () => {
  let bones = [];
  let boneImages = [];
  let imagesLoaded = false;
  let isMobile = false;
  let maxBones = 15; // Limit total bones for performance
  let frameSkipCounter = 0;
  let previousWidth = 0;
  let previousHeight = 0;

   const preload = (p5) => {
     // Load the two bone images from the assets folder
     const boneFillImg = p5.loadImage(require('./assets/images/round-bone-fill.png'), 
       () => {
         console.log('Loaded image: round-bone-fill.png');
       },
       () => {
         console.error('Failed to load image: round-bone-fill.png');
       }
     );
     
     const boneOutlineImg = p5.loadImage(require('./assets/images/round-bone.png'), 
       () => {
         console.log('Loaded image: round-bone.png');
       },
       () => {
         console.error('Failed to load image: round-bone.png');
       }
     );
     
     boneImages.push(boneFillImg, boneOutlineImg);
   };

  const setup = (p5, canvasParentRef) => {
    // Detect mobile more accurately
    isMobile = p5.windowWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Create canvas with consistent settings
    p5.createCanvas(p5.windowWidth, p5.windowHeight).parent(canvasParentRef);
    p5.pixelDensity(window.devicePixelRatio || 1); // Use device's native pixel density
    
    // Store initial dimensions
    previousWidth = p5.windowWidth;
    previousHeight = p5.windowHeight;
    
    // Set mobile flag on p5 instance for bone class access
    p5.isMobile = isMobile;
    
    // Adjust bone limits based on device
    if (isMobile) {
      maxBones = 3; // Only 3 bones on mobile
    } else {
      maxBones = 12; // Reduced from 15 for better performance
    }
    
    p5.angleMode(p5.DEGREES);
    console.log('Mobile detected:', isMobile, 'Max bones:', maxBones);
    imagesLoaded = true;
  };

  const getRandomX = (p5) => {
    // Distribute bones across the full width of the screen
    return p5.random(0, p5.windowWidth);
  };

  const draw = (p5) => {
    p5.clear();
    p5.background(255);

    if (!imagesLoaded || boneImages.length === 0) {
      return;
    }

    // Much slower bone creation for mobile
    const boneInterval = isMobile ? 60 : 25; // Very slow on mobile
    if (p5.frameCount % boneInterval === 0 && bones.length < maxBones) {
      const x = getRandomX(p5);
      const img = p5.random(boneImages);
      bones.push(new Bone(p5, img, x, isMobile));
    }

    // Process bones
    for (let i = bones.length - 1; i >= 0; i--) {
      bones[i].update();
      bones[i].display();

      if (bones[i].isOffScreen()) {
        bones.splice(i, 1);
      }
    }

    // Aggressive cleanup
    if (bones.length > maxBones) {
      bones = bones.slice(-maxBones);
    }
  };

  const windowResized = (p5) => {
    // Calculate the change in dimensions
    const widthChange = Math.abs(p5.windowWidth - previousWidth);
    const heightChange = Math.abs(p5.windowHeight - previousHeight);
    
    p5.resizeCanvas(p5.windowWidth, p5.windowHeight);
    isMobile = p5.windowWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    p5.isMobile = isMobile;
    
    // Update max bones based on new screen size
    maxBones = isMobile ? 3 : 12;
    
    // Only clear bones if there's a significant resize (orientation change or real window resize)
    // Small changes (< 100px) are likely just address bar showing/hiding on mobile
    if (widthChange > 100 || heightChange > 100) {
      bones = [];
      console.log('Significant resize detected, clearing bones');
    }
    
    // Update previous dimensions
    previousWidth = p5.windowWidth;
    previousHeight = p5.windowHeight;
  };

  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 0,
    }}>
      <Sketch
        preload={preload}
        setup={setup}
        draw={draw}
        windowResized={windowResized}
      />
    </div>
  );
};

export default BoneSketch;

